// components/slides/SlidesReviewStatus.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import { useSlidesStore } from './slidesStore';
import { reviewHasCompleteResult, reviewProgress } from '#lib/slides/reviewStatus';
import { needsBrandResearch } from '#lib/slides/brandResearch';
import { SlidesRunStatus } from './SlidesRunStatus';

const PRESENTATION = {
  running: { text: 'Review in progress', detail: 'You can keep browsing the slides.' },
  saving: { text: 'Saving review', detail: 'Waiting for save confirmation.' },
  completed: { text: 'Review completed', detail: 'Results saved.' },
  stopped: { text: 'Review stopped', detail: 'Results so far are kept' },
  failed: { text: 'Review failed', detail: 'Results so far are kept' },
  incomplete: { text: 'Review incomplete', detail: 'Not every point is judged yet' },
  unsaved: { text: 'Review not saved', detail: 'Your results are kept in this tab.' },
  // A run marked running that no open tab drives any more: to the reviewer it simply stopped.
  unknown: { text: 'Review stopped', detail: 'Results so far are kept' },
} as const;

// Before any result arrives the pace is unknown: a full review takes about twenty minutes.
const DEFAULT_MS_PER_PERCENT = 12_000;

/** Between saved results the running bar eases toward the end of the step under way, at this run's
 *  own pace, and never reaches it: only a saved result moves it past. */
function useEasedProgress(running: boolean, runId: string | undefined, exact: number, next: number): number {
  const [eased, setEased] = useState<{ runId?: string; value: number }>({ value: 0 });
  const mark = useRef({ runId: undefined as string | undefined, firstAt: 0, firstExact: 0, lastAt: 0, lastExact: -1 });
  useEffect(() => {
    const now = Date.now(), m = mark.current;
    if (m.runId !== runId) Object.assign(m, { runId, firstAt: now, firstExact: exact, lastAt: now, lastExact: exact });
    else if (exact !== m.lastExact) Object.assign(m, { lastAt: now, lastExact: exact });
  }, [runId, exact]);
  useEffect(() => {
    if (!running || next <= exact) return;
    const timer = setInterval(() => {
      const now = Date.now(), m = mark.current, done = exact - m.firstExact;
      const msPerPercent = done > 0.5 ? (m.lastAt - m.firstAt) / done : DEFAULT_MS_PER_PERCENT;
      const fraction = Math.min(0.9, (now - m.lastAt) / Math.max(1, msPerPercent * (next - exact)));
      setEased({ runId, value: exact + (next - exact) * fraction });
    }, 1000);
    return () => clearInterval(timer);
  }, [running, runId, exact, next]);
  return running && eased.runId === runId ? Math.max(exact, Math.min(eased.value, next)) : exact;
}

export function SlidesReviewStatus() {
  const deck = useSlidesStore(s => s.activeProjectId ? s.decks[s.activeProjectId] : undefined);
  const ownedRunId = useSlidesStore(s => s.activeProjectId ? s.runtime[s.activeProjectId]?.reviewRunId : undefined);
  // A review the worker runs is this tab's to show, though no turn of it runs here.
  const hostedSince = useSlidesStore(s => s.activeProjectId && s.hostedReview?.projectId === s.activeProjectId ? s.hostedReview.since : null);
  const hosted = hostedSince !== null;
  const live = deck ? reviewProgress(deck) : undefined;
  const liveRun = deck?.doc.reviewRun;
  const drivenHere = (r: typeof liveRun) => !!r && (r.id === ownedRunId || (hosted && r.host === 'server'));
  const eased = useEasedProgress(!!liveRun && liveRun.status === 'running' && drivenHere(liveRun), liveRun?.id, live?.exact ?? 0, live?.next ?? 0);
  const save = useSlidesStore(s => s.activeProjectId ? s.saveStates[s.activeProjectId] : undefined);
  const run = deck?.doc.reviewRun;
  if (!deck) return null;
  let status: keyof typeof PRESENTATION;
  // Sent to Fater, its run not opened yet (the brand research, the moments before): a run opened before the
  // hand-off is an earlier one.
  const starting = hosted && !(run?.round === deck.reviewRound && run.host === 'server' && Date.parse(run.startedAt) >= hostedSince - 60_000);
  if (starting) status = 'running';
  else if (run && run.round === deck.reviewRound) {
    if (run.status === 'running') status = drivenHere(run) ? 'running' : 'unknown';
    else if (run.status === 'completed') status = reviewHasCompleteResult(deck) ? 'completed' : 'incomplete';
    else status = run.status;
  } else if (!run && deck.status === 'REVIEWED' && reviewHasCompleteResult(deck)) status = 'completed';
  else if (deck.doc.review?.round === deck.reviewRound && Object.keys(deck.doc.review.points ?? {}).length) status = 'incomplete';
  else return null;
  if (status === 'completed' && (save?.savedVersion ?? -1) < deck.version) {
    status = save?.status === 'error' ? 'unsaved' : 'saving';
  }
  if (!Object.hasOwn(PRESENTATION, status)) status = 'unknown';
  const { text, detail } = PRESENTATION[status];
  // The bar stays on a stopped or unfinished review: it says how far it got.
  const progress = status === 'running' || status === 'stopped' || status === 'failed' || status === 'incomplete' || status === 'unknown'
    ? live : undefined;
  // Only a review with nothing done yet researches the brand first (launchSlidesReview).
  const step = starting ? `${needsBrandResearch(deck.doc.brandResearch) && !live?.exact ? 'Researching the brand' : 'Starting'}. You can close this tab.`
    : progress && status === 'running' ? (hosted ? `${progress.step}. You can close this tab.` : progress.step) : detail;
  return <SlidesRunStatus status={status} text={text} detail={step}
    showSaveRecovery={status === 'unsaved'} reviewTour progress={progress && (status === 'running' ? Math.min(99, eased) : progress.exact)} />;
}
